import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  warning?: boolean;
  helperText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, warning, helperText, leftIcon, rightIcon, fullWidth = true, className = '', id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const baseClasses = 'px-4 py-3 rounded-2xl bg-white/5 border text-white placeholder-text-muted focus:outline-none focus:ring-1 transition-all duration-200';
    const stateClasses = error
      ? 'border-red-500/50 focus:border-red-500/70 focus:ring-red-500/30'
      : warning
        ? 'border-yellow-500/50 focus:border-yellow-500/70 focus:ring-yellow-500/30'
        : 'border-white/10 focus:border-accent/50 focus:ring-accent/30';
    const widthClasses = fullWidth ? 'w-full' : '';
    const paddingClasses = `${leftIcon ? 'pl-10' : ''} ${rightIcon ? 'pr-10' : ''}`;

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && <label htmlFor={inputId} className="block text-sm font-medium text-text-muted mb-2">{label}</label>}
        <div className="relative">
          {leftIcon && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">{leftIcon}</div>}
          <input ref={ref} id={inputId} className={`${baseClasses} ${stateClasses} ${widthClasses} ${paddingClasses} ${className}`} {...props} />
          {rightIcon && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">{rightIcon}</div>}
        </div>
        {error && <p className="mt-1.5 text-sm text-red-400">{error}</p>}
        {helperText && !error && <p className="mt-1.5 text-sm text-text-secondary">{helperText}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
export default Input;
